import { PageHeader } from "@/components/PageHeader";
import { SortableHeader } from "@/components/SortableHeader";
import { api } from "@/lib/api";
import { formatDate, formatShortDate } from "@/lib/utils";
import type { AdminSubscription, StripeSubscriptionStatus } from "@/types";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, CreditCard } from "lucide-react";
import { useMemo, useState } from "react";

type SortField = "orgName" | "customerEmail" | "status" | "stripeEnv" | "planId" | "currentPeriodEnd" | "createdAt";
type SortOrder = "asc" | "desc";

type StripeEnvFilter = "all" | "sandbox" | "production";
const ENV_LABELS: Record<StripeEnvFilter, string> = {
  all: "All",
  sandbox: "Test",
  production: "Production",
};

const STATUS_COLORS: Record<string, string> = {
  active: "green",
  trialing: "blue",
  past_due: "amber",
  canceled: "red",
  incomplete: "zinc",
  incomplete_expired: "zinc",
  unpaid: "orange",
  paused: "zinc",
};

const ALL_STATUSES: StripeSubscriptionStatus[] = [
  "active",
  "trialing",
  "past_due",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused",
];

const STATUS_LABELS: Record<StripeSubscriptionStatus, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Past Due",
  canceled: "Canceled",
  incomplete: "Incomplete",
  incomplete_expired: "Incomplete Expired",
  unpaid: "Unpaid",
  paused: "Paused",
};

export function SubscriptionsPage() {
  const [selectedStatuses, setSelectedStatuses] = useState<StripeSubscriptionStatus[] | "all">(
    "all"
  );
  const [envFilter, setEnvFilter] = useState<StripeEnvFilter>("all");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  // Cursor stack for back navigation: array of starting_after values (undefined = first page)
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const currentCursor = cursorStack[cursorStack.length - 1];

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "createdAt" || field === "currentPeriodEnd" ? "desc" : "asc");
    }
  };

  const toggleStatus = (status: StripeSubscriptionStatus) => {
    if (selectedStatuses === "all") {
      setSelectedStatuses([status]);
    } else if (selectedStatuses.includes(status)) {
      const next = selectedStatuses.filter((s) => s !== status);
      setSelectedStatuses(next.length === 0 ? "all" : next);
    } else {
      setSelectedStatuses([...selectedStatuses, status]);
    }
    // Reset pagination when filter changes
    setCursorStack([undefined]);
  };

  const selectAll = () => {
    setSelectedStatuses("all");
    setCursorStack([undefined]);
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "subscriptions",
      {
        statuses: selectedStatuses,
        starting_after: currentCursor,
        env: envFilter,
      },
    ],
    queryFn: () =>
      api.getSubscriptions({
        statuses: selectedStatuses,
        limit: 20,
        starting_after: currentCursor,
        env: envFilter,
      }),
  });

  const hasMore = data?.hasMore ?? false;
  const nextCursor = data?.nextCursor ?? null;

  const subscriptions = useMemo(() => {
    const raw: AdminSubscription[] = data?.data ?? [];
    return [...raw].sort((a, b) => {
      let cmp = 0;
      if (sortField === "createdAt" || sortField === "currentPeriodEnd") {
        const aTime = new Date(a[sortField] || 0).getTime();
        const bTime = new Date(b[sortField] || 0).getTime();
        cmp = aTime - bTime;
      } else if (sortField === "orgName" || sortField === "customerEmail" || sortField === "status" || sortField === "stripeEnv" || sortField === "planId") {
        cmp = (a[sortField] ?? "").localeCompare(b[sortField] ?? "");
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }, [data, sortField, sortOrder]);

  const goNext = () => {
    if (nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
  };

  const goPrev = () => {
    setCursorStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const isOnFirstPage = cursorStack.length === 1;

  return (
    <div className="px-4 py-0">
      <PageHeader title="Subscriptions" />

      {/* Environment filter */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">Environment:</span>
          {(["all", "sandbox", "production"] as StripeEnvFilter[]).map((env) => (
            <button
              key={env}
              onClick={() => { setEnvFilter(env); setCursorStack([undefined]); }}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                envFilter === env
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600"
              }`}
            >
              {ENV_LABELS[env]}
            </button>
          ))}
        </div>
      </div>

      {/* Status filter */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">Filter by status:</span>
          <button
            onClick={selectAll}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedStatuses === "all"
                ? "bg-blue-600 text-white"
                : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600"
            }`}
          >
            All
          </button>
          {ALL_STATUSES.map((status) => {
            const isSelected =
              selectedStatuses !== "all" && selectedStatuses.includes(status);
            return (
              <button
                key={status}
                onClick={() => toggleStatus(status)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isSelected
                    ? "bg-blue-600 text-white"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600"
                }`}
              >
                {STATUS_LABELS[status]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Subscriptions Table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <SortableHeader field="orgName" label="Subscription" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="customerEmail" label="Customer" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="status" label="Status" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="stripeEnv" label="Env" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="planId" label="Plan" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="currentPeriodEnd" label="Current Period" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="createdAt" label="Created" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : subscriptions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-8 text-center">
                <CreditCard className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">No subscriptions found</p>
              </TableCell>
            </TableRow>
          ) : (
            subscriptions.map((sub) => (
              <TableRow key={sub.id} href={`/admin/subscriptions/${sub.id}`}>
                <TableCell className="font-medium">
                  <div>
                    {sub.orgName || "—"}
                    <div className="font-mono text-xs text-zinc-500">{sub.id}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    {sub.customerEmail || "—"}
                    {sub.customerId && (
                      <div className="font-mono text-xs text-zinc-500">{sub.customerId}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {sub.cancelAtPeriodEnd || sub.cancelAt ? (
                      <Badge color="orange">
                        Cancels {formatShortDate(sub.cancelAt ?? sub.currentPeriodEnd)}
                      </Badge>
                    ) : (
                      <Badge color={STATUS_COLORS[sub.status] ?? "zinc"}>
                        {STATUS_LABELS[sub.status] ?? sub.status.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge color={sub.stripeEnv === "sandbox" ? "amber" : "green"}>
                    {sub.stripeEnv === "sandbox" ? "Test" : "Prod"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {sub.planId ? (
                    <Badge color="zinc">{sub.planId.toUpperCase()}</Badge>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-zinc-500">
                  {formatDate(sub.currentPeriodEnd)}
                </TableCell>
                <TableCell className="text-zinc-500">
                  {formatDate(sub.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <ChevronRight className="inline h-5 w-5 text-zinc-400" />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {(!isOnFirstPage || hasMore) && (
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={goPrev} disabled={isOnFirstPage} outline>
            Previous
          </Button>
          <Button onClick={goNext} disabled={!hasMore} outline>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
